/**
 * Plugin System for Yap
 * 
 * Provides extensibility through:
 * - Dynamic plugin loading
 * - Plugin registry
 * - Tool registration
 * - Command registration
 * - UI panel registration
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { ToolType } from '../state/store';

/**
 * Plugin Manifest - describes a plugin's capabilities
 */
export interface PluginManifest {
    id: string;
    name: string;
    version: string;
    author: string;
    description: string;

    // Entry points
    tools?: PluginTool[];
    commands?: PluginCommand[];
    panels?: PluginPanel[];

    // Lifecycle
    onLoad?: () => void;
    onUnload?: () => void;
}

/**
 * Plugin Tool Definition
 */
export interface PluginTool {
    id: ToolType | string;
    name: string;
    icon: string;
    group: 'nav' | 'draw' | '3d' | 'custom';
    onActivate?: () => void;
    onDeactivate?: () => void;
    onPointerDown?: (point: [number, number, number], event: PointerEvent) => void;
    onPointerMove?: (point: [number, number, number], event: PointerEvent) => void;
    onPointerUp?: (point: [number, number, number], event: PointerEvent) => void;
}

/**
 * Plugin Command Definition
 */
export interface PluginCommand {
    name: string;
    aliases: string[];
    description: string;
    execute: (args: string[]) => { success: boolean; message: string };
}

/**
 * Plugin Panel Definition
 */
export interface PluginPanel {
    id: string;
    name: string;
    position: 'left' | 'right' | 'bottom';
    component: React.ComponentType;
}

/**
 * Plugin Instance (loaded plugin)
 */
export interface PluginInstance {
    manifest: PluginManifest;
    enabled: boolean;
    loadedAt: number;
}

/**
 * Plugin Store
 */
interface PluginState {
    plugins: Map<string, PluginInstance>;
    customTools: Map<string, PluginTool>;
    customCommands: Map<string, PluginCommand>;
    customPanels: Map<string, PluginPanel>;
    builtInPluginsLoaded: boolean;

    // Actions
    registerPlugin: (manifest: PluginManifest) => void;
    unregisterPlugin: (pluginId: string) => void;
    enablePlugin: (pluginId: string) => void;
    disablePlugin: (pluginId: string) => void;
    setBuiltInPluginsLoaded: (loaded: boolean) => void;

    // Getters
    getPlugin: (id: string) => PluginInstance | undefined;
    getAllPlugins: () => PluginInstance[];
    getToolsForGroup: (group: string) => PluginTool[];
    executeCommand: (name: string, args: string[]) => { success: boolean; message: string } | null;
}

/**
 * Plugin Registry Store
 */
export const usePluginStore = create<PluginState>((set, get) => ({
    plugins: new Map(),
    customTools: new Map(),
    customCommands: new Map(),
    customPanels: new Map(),
    builtInPluginsLoaded: false,

    registerPlugin: (manifest) => {
        const { plugins, customTools, customCommands, customPanels } = get();

        // Check if already registered
        if (plugins.has(manifest.id)) {
            console.warn(`Plugin ${manifest.id} already registered`);
            return;
        }

        // Create plugin instance
        const instance: PluginInstance = {
            manifest,
            enabled: true,
            loadedAt: Date.now(),
        };

        // Register tools
        manifest.tools?.forEach((tool) => {
            customTools.set(tool.id, tool);
        });

        // Register commands
        manifest.commands?.forEach((cmd) => {
            customCommands.set(cmd.name.toUpperCase(), cmd);
            cmd.aliases.forEach((alias) => {
                customCommands.set(alias.toUpperCase(), cmd);
            });
        });

        // Register panels
        manifest.panels?.forEach((panel) => {
            customPanels.set(panel.id, panel);
        });

        // Add to registry
        plugins.set(manifest.id, instance);

        // Call onLoad
        manifest.onLoad?.();

        // Fresh Map identities for the mutated collections so memoized
        // subscribers (usePlugins) see tool/command/panel changes.
        set({ plugins: new Map(plugins), customTools: new Map(customTools), customCommands: new Map(customCommands), customPanels: new Map(customPanels) });

        console.log(`Plugin loaded: ${manifest.name} v${manifest.version}`);
    },

    unregisterPlugin: (pluginId) => {
        const { plugins, customTools, customCommands, customPanels } = get();

        const instance = plugins.get(pluginId);
        if (!instance) return;

        // Remove tools
        instance.manifest.tools?.forEach((tool) => {
            customTools.delete(tool.id);
        });

        // Remove commands
        instance.manifest.commands?.forEach((cmd) => {
            customCommands.delete(cmd.name.toUpperCase());
            cmd.aliases.forEach((alias) => {
                customCommands.delete(alias.toUpperCase());
            });
        });

        // Remove panels
        instance.manifest.panels?.forEach((panel) => {
            customPanels.delete(panel.id);
        });

        // Call onUnload
        instance.manifest.onUnload?.();

        // Remove from registry
        plugins.delete(pluginId);

        // Fresh Map identities (see registerPlugin above).
        set({ plugins: new Map(plugins), customTools: new Map(customTools), customCommands: new Map(customCommands), customPanels: new Map(customPanels) });

        console.log(`Plugin unloaded: ${pluginId}`);
    },

    enablePlugin: (pluginId) => {
        const { plugins } = get();
        const instance = plugins.get(pluginId);
        if (instance) {
            instance.enabled = true;
            set({ plugins: new Map(plugins) });
        }
    },

    disablePlugin: (pluginId) => {
        const { plugins } = get();
        const instance = plugins.get(pluginId);
        if (instance) {
            instance.enabled = false;
            set({ plugins: new Map(plugins) });
        }
    },

    getPlugin: (id) => get().plugins.get(id),

    getAllPlugins: () => Array.from(get().plugins.values()),

    getToolsForGroup: (group) => {
        return Array.from(get().customTools.values()).filter((t) => t.group === group);
    },

    executeCommand: (name, args) => {
        const cmd = get().customCommands.get(name.toUpperCase());
        if (cmd) {
            return cmd.execute(args);
        }
        return null;
    },

    setBuiltInPluginsLoaded: (loaded) => {
        set({ builtInPluginsLoaded: loaded });
    },
}));

/**
 * Example Plugin: Grid Snap Tool
 */
export const GridSnapPlugin: PluginManifest = {
    id: 'grid-snap',
    name: 'Grid Snap Enhanced',
    version: '1.0.0',
    author: 'Yap Team',
    description: 'Enhanced grid snapping with configurable increments',

    commands: [
        {
            name: 'SNAPGRID',
            aliases: ['SG'],
            description: 'Set grid snap increment',
            execute: (args) => {
                const size = parseFloat(args[0]);
                if (isNaN(size) || size <= 0) {
                    return { success: false, message: 'Invalid grid size. Usage: SNAPGRID <size>' };
                }
                // Store in app state (simplified)
                (window as any).__gridSnapSize = size;
                return { success: true, message: `Grid snap set to ${size}` };
            },
        },
    ],

    onLoad: () => {
        console.log('Grid Snap plugin loaded');
    },
};

/**
 * Example Plugin: Measurement Tool
 */
export const MeasurementPlugin: PluginManifest = {
    id: 'measurement',
    name: 'Measurement Tools',
    version: '1.0.0',
    author: 'Yap Team',
    description: 'Distance and area measurement tools',

    commands: [
        {
            name: 'DIST',
            aliases: ['DI', 'DISTANCE'],
            description: 'Measure distance between two points',
            execute: () => {
                return { success: true, message: 'Click two points to measure distance' };
            },
        },
        {
            name: 'AREA',
            aliases: ['AA'],
            description: 'Calculate area of selected region',
            execute: () => {
                return { success: true, message: 'Select closed shape to calculate area' };
            },
        },
    ],

    tools: [
        {
            id: 'measure' as ToolType,
            name: 'Measure',
            icon: 'ruler',
            group: 'nav',
        },
    ],
};

/**
 * Load built-in plugins
 */
export function loadBuiltInPlugins() {
    const state = usePluginStore.getState();
    
    // Only load once, idempotent guard
    if (state.builtInPluginsLoaded) {
        return;
    }
    
    state.registerPlugin(GridSnapPlugin);
    state.registerPlugin(MeasurementPlugin);
    state.setBuiltInPluginsLoaded(true);
}

/**
 * Hook for plugin access
 */
export function usePlugins() {
    // Subscribe to the underlying Maps (stable identities, replaced only on
    // register/unregister) and materialize arrays in memos. The previous
    // selectors called actions / Array.from on every store snapshot.
    const pluginMap = usePluginStore((s) => s.plugins);
    const customToolMap = usePluginStore((s) => s.customTools);
    const plugins = useMemo(() => Array.from(pluginMap.values()), [pluginMap]);
    const customTools = useMemo(() => Array.from(customToolMap.values()), [customToolMap]);
    const registerPlugin = usePluginStore((s) => s.registerPlugin);
    const unregisterPlugin = usePluginStore((s) => s.unregisterPlugin);

    return {
        plugins,
        customTools,
        registerPlugin,
        unregisterPlugin,
    };
}
